import Button from "@pathwright/ui/src/components/button/Button"
import Link from "@pathwright/ui/src/components/link/Link"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Text from "@pathwright/ui/src/components/ui/Text"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import moment from "moment"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import { useMemo } from "react"
import styled from "styled-components"
import { useTotalMembersWithFiltersQuery } from "@pathwright/graphql-client/src/generated"
import { usePackage } from "../../package/usePackage"
import {
  SUBSCRIPTION_CHARGE_TYPE_INVOICE,
  SUBSCRIPTION_CHARGE_TYPE_NO_CHARGE,
  SUBSCRIPTION_INTERVAL_YEARLY
} from "../../subscription/constants"
import {
  BILLING_HELP_ARTICLES,
  BILLING_MODEL_HYBRID,
  BILLING_MODEL_PAYG,
  BILLING_MODEL_USER,
  TYPEFORM_EMBED,
  TYPEFORM_FORM_IDS
} from "../constants"
import { InvoiceType, SubscriptionType } from "../types"
import { getLineItemsForInvoice, usdNoDec } from "../utils"
import ManageBillingOverdueBill from "./ManageBillingOverdueBill"
import ManageBillingPlanTypeformModal from "./ManageBillingTypeformModalLazy"

const isExternal = (url) =>
  Boolean(url && (url.startsWith("http") || url.startsWith("mailto")))

const TOTAL_MEMBERS_QUERY_VARIABLES = {
  hasAccess: true,
  role_filter: { eq: "student" }
}

function getPlanName(invoiceType, planDetails) {
  switch (invoiceType) {
    case BILLING_MODEL_USER:
      return planDetails.name
    case BILLING_MODEL_HYBRID:
      return `Up to ${planDetails.included_members} ${pluralize(
        "member",
        planDetails.included_members
      )}`
    case BILLING_MODEL_PAYG:
      return "Pay as you go"
    default:
      return ""
  }
}

function getBillingHelpArticleUrl(invoiceType, planDetails) {
  const entry = BILLING_HELP_ARTICLES[invoiceType]
  if (typeof entry === "string" && entry.length > 0) return entry
  return entry[planDetails.learner_counting]
}

function getSubtitleText({
  invoice,
  isAnnual,
  isManual,
  isNoCharge,
  isCanceled,
  canceledMoment
}) {
  if (isManual || isNoCharge) {
    return `Your ${isAnnual ? "annual" : "monthly"} bill estimate`
  }
  if (isCanceled) {
    return canceledMoment.isAfter()
      ? `Subscription cancelled. Members will retain access until ${canceledMoment.format(
          "MMM Do, YYYY"
        )}.`
      : `Subscription cancelled on ${canceledMoment.format("MMM Do, YYYY")}.`
  }
  return `Your ${
    isAnnual ? "annual" : "monthly"
  } bill estimate for use between ${moment(invoice.cycle_start).format(
    "MMM Do, YYYY"
  )} and ${moment(invoice.cycle_end).format("MMM Do, YYYY")}.`
}

const StyledContainer = styled.div`
  padding: 20px 0;
  border-radius: 5px;

  position: relative;
  header,
  section {
    margin-bottom: 30px;
  }

  header {
    text-align: center;
  }
`

export const StyledToolbox = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  border-radius: 15px;
  border: 1px solid ${(p) => p.planColor};

  i {
    color: ${(p) => p.planColor};
  }
`

export const StyledSubtitle = styled(Text.Meta)`
  max-width: 400px;
  font-size: 1em;
  display: inline-block;
`

const CategorySection = styled.div`
  position: relative;

  &:nth-child(n + 3):before {
    content: "";
    background-color: ${(p) => (p.showBorder ? "lightgray" : "transparent")};
    position: absolute;
    top: 0;
    left: 0;
    left: 30px;
    right: calc(100% - 30px);
    width: calc(100% - 60px);
    height: 1px;
  }
`

export const Category = styled.div`
  padding: 10px 30px 0px;
`

const CategoryTitle = styled.div`
  padding: 10px 0 7px;
  display: flex;
  justify-content: space-between;
`

const CategoryItems = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 20px 2rem;
`

export const CategoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  &:last-child {
    border-bottom: none;
  }

  > * {
    padding: 3px;

    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
    }
  }
`

export const LinkItem = styled.div`
  display: flex;
  padding: 5px 0;

  a.Link {
    font-size: 0.9em;
    color: rgba(0, 0, 0, 0.4);
    text-decoration: underline;
  }
`

export const StyledPricing = styled(Text.Meta)`
  font-size: 0.9em;
  color: #333;
`

function CategoryLinkRow({ link, invoice }) {
  if (!link) return null
  if (link.url?.includes(TYPEFORM_EMBED)) {
    return (
      <ManageBillingPlanTypeformModal
        renderTrigger={() => (
          <LinkItem>
            <Link>Review or update your plan</Link>
          </LinkItem>
        )}
        title="Review or update your plan"
        typeformId={TYPEFORM_FORM_IDS.PLAN_UPDATE}
        invoice={invoice}
      />
    )
  }
  return (
    <LinkItem>
      <Link
        {...(isExternal(link.url) ? { href: link.url } : { to: link.url })}
        target="_blank"
      >
        {link.label}
      </Link>
    </LinkItem>
  )
}

CategoryLinkRow.displayName = "CategoryLinkRow"
CategoryLinkRow.propTypes = {
  link: PropTypes.object,
  invoice: InvoiceType
}

export function ManageBillingInvoiceItemDetails({
  subscription,
  invoice,
  loading: parentLoading = false,
  schoolScholarship
}) {
  const { data, loading: membersLoading } = useTotalMembersWithFiltersQuery({
    variables: TOTAL_MEMBERS_QUERY_VARIABLES
  })

  const { packageData } = usePackage({
    dataSource: "assignment"
  })

  const totalMembersWithAccess = get(data, "school.members.total")

  const lineItemsForInvoice = useMemo(() => {
    if (!invoice?.invoice_json) return {}
    return getLineItemsForInvoice(
      invoice,
      subscription,
      totalMembersWithAccess,
      schoolScholarship,
      packageData
    )
  }, [
    invoice,
    subscription,
    totalMembersWithAccess,
    schoolScholarship,
    packageData
  ])

  const loading = parentLoading || membersLoading

  const invoiceJson = invoice?.invoice_json
  const invoiceType = invoiceJson?.billing_model
  const planDetails = invoiceJson?.plan_details

  const planName = useMemo(
    () => (planDetails ? getPlanName(invoiceType, planDetails) : ""),
    [invoiceType, planDetails]
  )

  const billingHelpArticle = useMemo(
    () =>
      planDetails
        ? getBillingHelpArticleUrl(invoiceType, planDetails)
        : undefined,
    [invoiceType, planDetails]
  )

  const isAnnual =
    invoiceJson?.billing_interval === SUBSCRIPTION_INTERVAL_YEARLY
  const isManual =
    get(subscription, "charge_type") === SUBSCRIPTION_CHARGE_TYPE_INVOICE
  const isNoCharge =
    get(subscription, "charge_type") === SUBSCRIPTION_CHARGE_TYPE_NO_CHARGE
  const canceledDtime = get(subscription, "subscription.canceled_dtime")
  const isCanceled = !isEmpty(canceledDtime)

  const subtitle = useMemo(() => {
    if (!invoice || !invoiceJson) return ""
    return getSubtitleText({
      invoice,
      isAnnual,
      isManual,
      isNoCharge,
      isCanceled,
      canceledMoment: moment(canceledDtime)
    })
  }, [
    invoice,
    invoiceJson,
    isAnnual,
    isManual,
    isNoCharge,
    isCanceled,
    canceledDtime
  ])

  if (loading) {
    return <LoadingCircle />
  }

  if (!invoice || !invoiceJson) {
    return (
      <StyledContainer>
        <header>
          <StyledSubtitle>
            {"We couldn't locate your plan information. Please contact "}
            <Link href="mailto:hello@pathwright.com">
              hello@pathwright.com
            </Link>{" "}
            to correct your plan configuration.
          </StyledSubtitle>
        </header>
      </StyledContainer>
    )
  }

  const planColor = planDetails ? `#${planDetails.color}` : "black"

  return (
    <StyledContainer>
      <header>
        <StyledToolbox planColor={planColor}>
          <Pathicon icon="toolbox" />
        </StyledToolbox>
        <Text.H3
          style={{
            color: planDetails ? planColor : null,
            marginBottom: "0.7rem"
          }}
        >
          {planName}
        </Text.H3>
        <StyledSubtitle>{subtitle}</StyledSubtitle>
      </header>
      {Object.entries(lineItemsForInvoice).map(([key, categories], i) => (
        <CategorySection showBorder={key !== "totals"} key={key}>
          {categories.map(
            ({ label, total, icon, meta, link, items }, categoryIndex) => (
              <Category
                key={`${key}-${categoryIndex}`}
                style={{
                  backgroundColor: key === "totals" ? "whitesmoke" : ""
                }}
              >
                <CategoryTitle>
                  <div style={{ display: "flex" }}>
                    <Pathicon
                      icon={icon || "toolbox"}
                      style={{
                        marginRight: "1rem",
                        color: icon ? "rgba(0, 0, 0, 0.4)" : "rgba(0,0,0,0)"
                      }}
                    />
                    <Text.H4
                      style={{
                        fontWeight: key === "totals" ? "400" : "700"
                      }}
                    >
                      {label}{" "}
                      <Text.Meta
                        style={{
                          fontSize: "0.8em",
                          color: "rgba(0,0,0,0.4)",
                          fontWeight: "400"
                        }}
                      >
                        {meta}
                      </Text.Meta>
                    </Text.H4>
                  </div>
                  <StyledPricing>{usdNoDec(total)}</StyledPricing>
                </CategoryTitle>
                {!!items && (
                  <CategoryItems>
                    {items.map(({ label: itemLabel }, itemIndex) => (
                      <CategoryItem
                        key={`${key}-${categoryIndex}-${itemIndex}`}
                      >
                        <Text.Meta>{itemLabel}</Text.Meta>
                      </CategoryItem>
                    ))}
                    <CategoryLinkRow link={link} invoice={invoice} />
                  </CategoryItems>
                )}
              </Category>
            )
          )}
        </CategorySection>
      ))}
      <ManageBillingOverdueBill>
        {(OverdueComponent) => (
          <StyledContainer style={{ backgroundColor: "whitesmoke" }}>
            <Category>{OverdueComponent}</Category>
          </StyledContainer>
        )}
      </ManageBillingOverdueBill>
      <Category>
        <Text.Meta>
          Have questions about your bill?{" "}
          <Button styleType="text" href={billingHelpArticle} target="_blank">
            Learn more.
          </Button>
        </Text.Meta>
      </Category>
    </StyledContainer>
  )
}

ManageBillingInvoiceItemDetails.displayName = "ManageBillingInvoiceItemDetails"
ManageBillingInvoiceItemDetails.propTypes = {
  subscription: SubscriptionType,
  invoice: InvoiceType,
  loading: PropTypes.bool,
  schoolScholarship: PropTypes.object
}

export default ManageBillingInvoiceItemDetails
